// Offline detection + durable two-way sync engine (push local queue, pull cloud changes).
// Emits status via a subscribable store.
import { api } from "./api";
import { idb } from "./idb";

let listeners = new Set();
let state = {
  online: navigator.onLine, syncing: false, pending: 0, lastError: null,
  lastSync: null,                       // last fully successful cycle
  push: null,                           // { at, accepted, failed, error }
  pull: null,                           // { at, counts, downloaded, full, error }
  newOrders: 0,                         // GuyanaDiscounts orders not yet fulfilled/cancelled
  pullVersion: 0,                       // bumps whenever local data changed from a pull
};

function emit() { listeners.forEach((l) => l({ ...state })); }
export function subscribe(fn) { listeners.add(fn); fn({ ...state }); return () => listeners.delete(fn); }
export function getState() { return { ...state }; }

async function refreshPending() {
  const q = await idb.all("sync_queue");
  state.pending = (q || []).length;
  emit();
}

async function ping() {
  try { await api.get("/health", { timeout: 4000 }); return true; } catch { return false; }
}

export async function enqueue(record) {
  await idb.put("sync_queue", { ...record, attempts: 0, next_retry_at: 0, last_error: null, queued_at: Date.now() });
  await refreshPending();
}

async function pushQueue(deviceId) {
  const queue = (await idb.all("sync_queue")) || [];
  const now = Date.now();
  const due = queue.filter((r) => (r.next_retry_at || 0) <= now);
  if (due.length === 0) return { at: new Date().toISOString(), accepted: 0, failed: 0, skipped: queue.length };
  const records = due.map((r) => ({ entity: r.entity, uuid: r.id, idempotency_key: r.idempotency_key, payload: r.payload }));
  const { data } = await api.post("/sync/push", { device_id: deviceId, records });
  for (const res of data.results) {
    const item = due.find((d) => d.id === res.uuid);
    if (!item) continue;
    if (res.status === "accepted" || res.status === "duplicate") await idb.del("sync_queue", item.id);
    else {
      item.attempts = (item.attempts || 0) + 1;
      item.last_error = res.error || "Unknown error";
      item.next_retry_at = Date.now() + Math.min(60000, 1000 * Math.pow(2, item.attempts));
      await idb.put("sync_queue", item);
    }
  }
  return { at: data.synced_at, accepted: data.accepted, failed: data.failed, errors: data.results.filter((r) => r.status === "error").map((r) => r.error) };
}

async function pullChanges(deviceId, full = false) {
  const meta = await idb.get("meta", "last_pull");
  const since = full ? undefined : meta?.value;
  const { data } = await api.get("/sync/pull", { params: { since, device_id: deviceId } });
  if (data.full) await idb.clear("products");
  if (data.products.length) {
    // keep local stock honest for sales still waiting in the upload queue
    const pendingSales = ((await idb.all("sync_queue")) || []).filter((q) => q.entity === "sale");
    const pendingQty = {};
    pendingSales.forEach((q) => (q.payload?.items || []).forEach((i) => { pendingQty[i.product_id] = (pendingQty[i.product_id] || 0) + i.quantity; }));
    await idb.putAll("products", data.products.map((p) => (pendingQty[p.id] ? { ...p, quantity: p.quantity - pendingQty[p.id] } : p)));
  }
  if (data.customers?.length) await idb.putAll("customers", data.customers);
  if (data.online_orders?.length) await idb.putAll("online_orders", data.online_orders);
  await idb.put("meta", { id: "cats", value: data.categories });
  if (data.tax_settings) {
    const ctx = (await idb.get("meta", "context"))?.value;
    if (ctx) await idb.put("meta", { id: "context", value: { ...ctx, tax: data.tax_settings } });
  }
  await idb.put("meta", { id: "last_pull", value: data.server_time });
  const orders = (await idb.all("online_orders")) || [];
  state.newOrders = orders.filter((o) => !["fulfilled", "cancelled"].includes(o.status)).length;
  state.pullVersion += 1;
  return { at: data.server_time, counts: data.counts, downloaded: Object.values(data.counts).reduce((a, b) => a + b, 0), full: data.full };
}

// One full cycle: push local queue, then pull cloud changes. `manual` forces a full catalog refresh.
export async function runSync(deviceId = "web-register", { manual = false } = {}) {
  if (state.syncing) return state;
  const reachable = await ping();
  state.online = reachable;
  if (!reachable) { if (manual) state.lastError = "Cannot reach the cloud — check the internet connection"; emit(); return state; }
  state.syncing = true; emit();
  let ok = true;
  try {
    try { state.push = await pushQueue(deviceId); if (state.push.failed > 0) ok = false; }
    catch (e) { state.push = { at: new Date().toISOString(), accepted: 0, failed: 0, error: "Upload failed — connection error" }; ok = false; }
    try { state.pull = await pullChanges(deviceId, manual); }
    catch (e) { state.pull = { at: new Date().toISOString(), counts: {}, downloaded: 0, error: e.response?.data?.detail || "Download failed — connection error" }; ok = false; }
    if (ok) { state.lastSync = new Date().toISOString(); state.lastError = null; await idb.put("meta", { id: "last_sync", value: state.lastSync }); }
    else state.lastError = state.push?.error || state.pull?.error || `${state.push?.failed || 0} record(s) failed — will retry`;
  } finally {
    state.syncing = false;
    await refreshPending();
  }
  return { ...state };
}

export const syncNow = (deviceId) => runSync(deviceId, { manual: true });

let started = false;
export function startSyncEngine(deviceId) {
  if (started) return;
  started = true;
  (async () => {
    state.lastSync = (await idb.get("meta", "last_sync"))?.value || null;
    const orders = (await idb.all("online_orders")) || [];
    state.newOrders = orders.filter((o) => !["fulfilled", "cancelled"].includes(o.status)).length;
    await refreshPending();
  })();
  window.addEventListener("online", () => { state.online = true; emit(); runSync(deviceId); });
  window.addEventListener("offline", () => { state.online = false; emit(); });
  setInterval(async () => {
    const r = await ping();
    if (r !== state.online) { state.online = r; emit(); }
    if (r) runSync(deviceId);
  }, 30000);
  runSync(deviceId);
}
